import { LocalDate } from "@js-joda/core";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { SalaryNotice } from "model/Salary";
import { Api } from "services/api/api.service";

export type MonthlySalaryFetchState =
  | { status: "idle" }
  | { status: "loading" }
  | {
      status: "loaded";
      salaryNotices: SalaryNotice[];
      totalPages: number;
    }
  | { status: "error" };

export type MonthlySalaryLoadedState = Extract<
  MonthlySalaryFetchState,
  { status: "loaded" }
>;

interface UseMonthlySalaryReturn {
  fetchState: MonthlySalaryFetchState;
  getSalaries: () => void;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
}

const FETCH_AMOUNT = 25;

export const useMonthlySalary = (
  companyId: number | undefined,
  currentDate: LocalDate,
): UseMonthlySalaryReturn => {
  const [page, setPage] = useState(0);
  const [fetchState, setFetchState] = useState<MonthlySalaryFetchState>({
    status: "idle",
  });
  const latestRequestIdRef = useRef(0);

  const getSalaries = useCallback((): void => {
    if (!companyId) {
      latestRequestIdRef.current += 1;
      setFetchState({ status: "idle" });
      return;
    }

    const requestId = ++latestRequestIdRef.current;
    setFetchState({ status: "loading" });

    const fetchSalaries = async (): Promise<void> => {
      try {
        const res = await Api().company.salary.getSalaries(
          companyId,
          page,
          FETCH_AMOUNT,
          currentDate.year(),
          currentDate.monthValue(),
        );
        if (requestId !== latestRequestIdRef.current) return;
        setFetchState({
          status: "loaded",
          salaryNotices: res.data.salaryNotices,
          totalPages: Math.ceil(res.data.total / FETCH_AMOUNT),
        });
      } catch {
        if (requestId !== latestRequestIdRef.current) return;
        setFetchState({ status: "error" });
      }
    };

    void fetchSalaries();
  }, [companyId, page, currentDate]);

  useEffect(() => {
    getSalaries();
  }, [getSalaries]);

  return {
    fetchState,
    getSalaries,
    page,
    setPage,
  };
};
